// src/firebase/orgs.js
// Multi-tenancy: minimal org helpers. See CLAUDE.md / the multi-tenancy plan
// for the full model. Every existing account/cohort/submission belongs to
// DEFAULT_ORG_ID (Aileen's org) - this matches the fallback baked into
// firestore.rules/storage.rules, so writing this value explicitly here keeps
// new documents consistent with how pre-rollout documents are already
// treated, rather than changing any existing behavior.
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';

export const DEFAULT_ORG_ID = 'aileen-atx';

let cachedOrgId = null;
let cachedForUid = null;

/**
 * Resolves the signed-in user's own orgId by reading their Firestore
 * profile, cached per-uid for the rest of the session (so per-org settings
 * lookups, which happen frequently, don't each pay for a Firestore read).
 * The cache naturally invalidates itself when a different uid signs in.
 *
 * Falls back to DEFAULT_ORG_ID when signed out, or when the profile doc
 * doesn't exist yet (e.g. mid-registration, before the new user's own doc
 * has been written) - callers with a known org context from elsewhere
 * (like registration.js, which already has the cohort's orgId) should pass
 * it explicitly rather than relying on this resolver.
 */
export const getCurrentUserOrgId = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) return DEFAULT_ORG_ID;
  if (cachedForUid === uid && cachedOrgId) return cachedOrgId;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    cachedOrgId = snap.data()?.orgId || DEFAULT_ORG_ID;
    cachedForUid = uid;
    return cachedOrgId;
  } catch (error) {
    console.warn('Could not resolve current user org, defaulting:', error);
    return DEFAULT_ORG_ID;
  }
};
